/**
 * The ONE start-a-one-person-test definition (plan 2026-10-15-004 §4.1), called
 * by the member panel and by each person row's Test button so they cannot
 * drift: same request, same adoption after an abort, same pending / working
 * rule, same store.
 *
 * No effect in here (selectors + a click handler), so StrictMode has nothing to
 * double-fire. The row button must not start a log query per person, so
 * "working" comes from the sends list the Dispatch log already polls
 * (`memberTestWorking`); the panel alone reads the person's log row.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../../_shared/hooks';
import type { CampaignMember } from '../../../_shared/launcher-campaign.types';
import { listPlaySendsRequest, listSendLogPageRequest } from '../../api-client';
import { newestTestDispatchId, playSendsQueryKey } from '../../dispatch-inspector-logic';
import { memberTestFailureOf, memberTestWorking, type MemberTestFailure } from '../../member-test-logic';
import {
  clearMemberFailure,
  clearTestPending,
  markTestPending,
  memberTestKey,
  recordMemberFailure,
  recordMemberTest,
  useMemberTestStore,
} from '../../member-test-store';
import { sendMemberTest } from '../../mutations';
import type { PlaybooksPlaySendsResponse } from '../../schemas';

type TestSendEntry = PlaybooksPlaySendsResponse['testSends'][number];

export interface UseMemberTestArgs {
  playId: string;
  member: CampaignMember;
  /** `tally.routedMemberIds`; `null` while the ladder is unavailable. */
  routedMemberIds: string[] | null;
  /** `memberTestDisabledReason(...)` for this person's group, or `null` when a test can be sent. */
  disabledReason: string | null;
}

export interface UseMemberTest {
  /** Sends the test. Refusals land in the store as `failure`; nothing throws. */
  start: () => Promise<void>;
  /** The request itself is in flight. */
  pending: boolean;
  /** This person's latest test, if one was started in this session. */
  latest: { dispatchId: string; startedAt: string } | undefined;
  /** The latest test's entry in the shared sends list, once listed (its `summary` carries the outcome). */
  testSend: TestSendEntry | undefined;
  /** The latest test is still going (from the shared sends list). */
  working: boolean;
  disabled: boolean;
  /** The words the last refused start left, or `null`. */
  failure: MemberTestFailure | null;
}

export function useMemberTest({ playId, member, routedMemberIds, disabledReason }: UseMemberTestArgs): UseMemberTest {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const key = memberTestKey(playId, member.id);

  const pending = useMemberTestStore((state) => state.pendingKeys[key] === true);
  const latest = useMemberTestStore((state) => state.testsByKey[key]);
  const failure = useMemberTestStore((state) => state.failuresByKey[key]) ?? null;

  // The sends query the Dispatch log polls (it owns the 5 s interval); subscribing without an interval of our own keeps it ONE poll however many rows mount this hook.
  const sendsQuery = useQuery({
    queryKey: playSendsQueryKey(playId),
    queryFn: () => listPlaySendsRequest(apiFetch, playId),
    enabled: latest !== undefined,
  });
  // A sends list that cannot be read must not leave Test greyed forever.
  const working = !(sendsQuery.isError && sendsQuery.data === undefined) && memberTestWorking(latest, sendsQuery.data);
  const disabled = disabledReason !== null || pending || working || routedMemberIds === null;

  /**
   * Every test dispatch the server listed BEFORE this request, or `null` when
   * that could not be read (then nothing can be proven new, so nothing is adopted).
   */
  async function dispatchIdsBeforeRequest(): Promise<ReadonlySet<string> | null> {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: playSendsQueryKey(playId),
        queryFn: () => listPlaySendsRequest(apiFetch, playId),
        staleTime: 0,
      });
      return new Set(data.testSends.map((t) => t.dispatchId));
    } catch {
      return null;
    }
  }

  /**
   * After a request the browser gave up on: the server may have accepted it.
   * Adopt only a dispatch that did not exist before the request AND whose one
   * row is this person's — an older one-person test is never this request's.
   */
  async function adoptNewTestIfMine(before: ReadonlySet<string> | null): Promise<void> {
    if (before === null) return;
    try {
      await queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
      const newest = newestTestDispatchId(
        queryClient.getQueryData<PlaybooksPlaySendsResponse>(playSendsQueryKey(playId)),
      );
      if (newest === null || before.has(newest)) return;
      const page = await listSendLogPageRequest(apiFetch, playId, newest, { status: 'all', limit: 2 });
      if (page.rows.length === 1 && page.rows[0].campaign_member_id === member.id) {
        recordMemberTest(playId, member.id, newest);
        clearMemberFailure(playId, member.id);
      }
    } catch {
      // Could not look: leave the "still starting" sentence; never guess.
    }
  }

  async function start(): Promise<void> {
    if (routedMemberIds === null || disabled) return;
    clearMemberFailure(playId, member.id);
    // `playId` and `member.id` are the click-time values: the request may settle after the rep moved on, and then everything below lands under THIS play and person.
    markTestPending(playId, member.id);
    let before: ReadonlySet<string> | null = null;
    try {
      before = await dispatchIdsBeforeRequest();
      const response = await sendMemberTest(apiFetch, playId, routedMemberIds, member.id);
      recordMemberTest(playId, member.id, response.dispatchId);
      // The Dispatch log lists the new "Test · 1 person" card; the panel is the result surface.
      void queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
    } catch (error) {
      const words = memberTestFailureOf(error);
      if (words !== null) {
        recordMemberFailure(playId, member.id, words);
        if (words.adoptNewestTest) await adoptNewTestIfMine(before);
      }
    } finally {
      clearTestPending(playId, member.id);
    }
  }

  const testSend = sendsQuery.data?.testSends.find((t) => t.dispatchId === latest?.dispatchId);
  return { start, pending, latest, testSend, working, disabled, failure };
}
